/**
 * Always returns a SAFE string from any axios/fetch error shape.
 *
 * Why this exists: FastAPI Pydantic validation errors (HTTP 422) come back as
 *   { detail: [ { type, loc, msg, input, ctx }, ... ] }
 * If a component does `setError(e?.response?.data?.detail)` and then
 * `<p>{error}</p>`, React crashes with #31 ("Objects are not valid as a React
 * child"). Use this helper everywhere instead of touching `.detail` directly.
 */
export function errMsg(err, fallback = "Something went wrong") {
  if (!err) return fallback;
  if (typeof err === "string") return err;

  // Axios-shaped error: { response: { data: { detail: ... } } }
  const detail = err?.response?.data?.detail ?? err?.data?.detail ?? err?.detail;
  if (detail) {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const parts = detail
        .map(d => {
          if (!d) return "";
          if (typeof d === "string") return d;
          // Pydantic v2 ValidationError row
          const where = Array.isArray(d.loc) ? d.loc.filter(x => x !== "body").join(".") : "";
          const msg = d.msg || d.message || d.type || "";
          return where ? `${where}: ${msg}` : msg;
        })
        .filter(Boolean);
      if (parts.length) return parts.join(" · ");
    }
    if (typeof detail === "object") {
      return detail.msg || detail.message || JSON.stringify(detail);
    }
  }

  if (typeof err.message === "string" && err.message) return err.message;
  if (typeof err === "object") {
    try { return JSON.stringify(err); } catch { /* noop */ }
  }
  return String(err) || fallback;
}

export default errMsg;
